import React, { useState, useEffect, useCallback } from "react";
import { X, FileSpreadsheet, RefreshCw, Download, FileCode2, Pencil, Check } from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";
import { formatIndianCurrency } from "../utils/Const";

const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
};
const today = () => new Date().toLocaleDateString("en-CA");

export default function GstReportModal({ onClose }) {
  const { restaurantId } = useAuth();
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingGstin, setEditingGstin] = useState(false);
  const [gstinDraft, setGstinDraft] = useState("");
  const [downloading, setDownloading] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/gst?from=${range.from}&to=${range.to}`);
      setReport(res.data);
      setGstinDraft(res.data.gstin || "");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load GST report");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const saveGstin = async () => {
    try {
      await api.put(`/user/${restaurantId}`, { gstin: gstinDraft.toUpperCase().trim() });
      toast.success("GSTIN saved");
      setEditingGstin(false);
      fetchReport();
    } catch {
      toast.error("Failed to save GSTIN");
    }
  };

  const downloadTally = async (format) => {
    setDownloading(format);
    try {
      const res = await api.get(
        `/reports/gst/tally?from=${range.from}&to=${range.to}&format=${format}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tally-sales-${range.from}_to_${range.to}.${format === "csv" ? "csv" : "xml"}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(format === "csv" ? "CSV downloaded" : "Tally XML downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setDownloading(null);
    }
  };

  const fmt = (n) => `₹${formatIndianCurrency(n || 0)}`;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center">
              <FileSpreadsheet size={15} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">GST Report & Tally Export</h2>
              <p className="text-[10px] text-slate-400">Rate-wise tax summary · GSTR-1 B2C ready</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <X size={14} />
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/50">
          <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500" />
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500" />

          {/* GSTIN */}
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-[10px] font-bold text-slate-400 uppercase">GSTIN</span>
            {editingGstin ? (
              <>
                <input value={gstinDraft} onChange={e => setGstinDraft(e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5"
                  className="border border-slate-200 rounded-lg px-2 py-1 text-xs font-mono w-40 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                <button onClick={saveGstin} className="w-6 h-6 flex items-center justify-center rounded-md bg-emerald-100 text-emerald-600">
                  <Check size={11} />
                </button>
              </>
            ) : (
              <>
                <code className="text-xs font-mono text-slate-600">{report?.gstin || "not set"}</code>
                <button onClick={() => setEditingGstin(true)} className="w-6 h-6 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 text-slate-500">
                  <Pencil size={10} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : report && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Taxable value", value: fmt(report.totals.taxableValue) },
                  { label: "CGST", value: fmt(report.totals.cgst) },
                  { label: "SGST", value: fmt(report.totals.sgst) },
                  { label: "Invoices", value: report.totals.invoices },
                ].map(c => (
                  <div key={c.label} className="rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5">
                    <p className="text-[9px] font-black uppercase tracking-wider text-slate-400">{c.label}</p>
                    <p className="text-sm font-black text-slate-800 mt-0.5">{c.value}</p>
                  </div>
                ))}
              </div>

              {/* Rate-wise table */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Rate-wise summary (GSTR-1 B2C)</p>
                <div className="rounded-xl border border-slate-200 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-[9px] font-black uppercase tracking-wider text-slate-400">
                        <th className="px-3 py-2 text-left">GST rate</th>
                        <th className="px-3 py-2 text-right">Taxable value</th>
                        <th className="px-3 py-2 text-right">CGST</th>
                        <th className="px-3 py-2 text-right">SGST</th>
                        <th className="px-3 py-2 text-right">Total tax</th>
                        <th className="px-3 py-2 text-right">Invoices</th>
                        <th className="px-3 py-2 text-right">Gross</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byRate.length === 0 ? (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No bills in this period</td></tr>
                      ) : report.byRate.map(r => (
                        <tr key={r.rate} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-bold text-slate-700">{r.rate}%</td>
                          <td className="px-3 py-2 text-right">{fmt(r.taxableValue)}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.cgst)}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.sgst)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{fmt(r.totalTax)}</td>
                          <td className="px-3 py-2 text-right text-slate-500">{r.invoices}</td>
                          <td className="px-3 py-2 text-right font-bold">{fmt(r.grossValue)}</td>
                        </tr>
                      ))}
                      {report.byRate.length > 0 && (
                        <tr className="border-t border-slate-200 bg-slate-50 font-black text-slate-800">
                          <td className="px-3 py-2">Total</td>
                          <td className="px-3 py-2 text-right">{fmt(report.totals.taxableValue)}</td>
                          <td className="px-3 py-2 text-right">{fmt(report.totals.cgst)}</td>
                          <td className="px-3 py-2 text-right">{fmt(report.totals.sgst)}</td>
                          <td className="px-3 py-2 text-right">{fmt(report.totals.totalTax)}</td>
                          <td className="px-3 py-2 text-right">{report.totals.invoices}</td>
                          <td className="px-3 py-2 text-right">{fmt(report.totals.grossValue)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Day-wise table */}
              {report.byDay.length > 0 && (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Day-wise summary</p>
                  <div className="rounded-xl border border-slate-200 overflow-x-auto max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0">
                        <tr className="bg-slate-50 text-[9px] font-black uppercase tracking-wider text-slate-400">
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-right">Taxable</th>
                          <th className="px-3 py-2 text-right">CGST</th>
                          <th className="px-3 py-2 text-right">SGST</th>
                          <th className="px-3 py-2 text-right">Invoices</th>
                          <th className="px-3 py-2 text-right">Gross</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.byDay.map(d => (
                          <tr key={d.date} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 font-semibold text-slate-700">{d.date}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(d.taxableValue)}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(d.cgst)}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(d.sgst)}</td>
                            <td className="px-3 py-1.5 text-right text-slate-500">{d.invoices}</td>
                            <td className="px-3 py-1.5 text-right font-bold">{fmt(d.grossValue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer: exports */}
        <div className="px-5 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">
            Tally XML imports as day-wise Sales vouchers (Gateway → Import Data). Ledgers: Cash, Restaurant Sales, Output CGST/SGST.
          </p>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => downloadTally("csv")}
              disabled={downloading !== null}
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              {downloading === "csv" ? <RefreshCw size={12} className="animate-spin" /> : <Download size={12} />}
              CSV (Excel)
            </button>
            <button
              onClick={() => downloadTally("xml")}
              disabled={downloading !== null}
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {downloading === "xml" ? <RefreshCw size={12} className="animate-spin" /> : <FileCode2 size={12} />}
              Tally XML
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
